import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    gtag?: (
      command: 'event',
      eventName: string,
      eventParameters?: {
        event_callback?: () => void;
        event_timeout?: number;
        [key: string]: unknown;
      }
    ) => void;
  }
}

const Banner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  const sparkElements = useMemo(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1200;
    let count = 48;
    if (width <= 640) count = 9;
    else if (width <= 1024) count = 30;

    const sparks = Array.from({ length: count }).map((_, i) => {
      const top = Math.random() * 100;
      const left = Math.random() * 100;
      const size = Math.random() * 2 + 1;
      const delay = Math.random() * -6;
      const duration = Math.random() * 3 + 2;
      const opacity = 0.35 + Math.random() * 0.65;
      return { id: i, top, left, size, delay, duration, opacity };
    });

    return (
      <div className="absolute inset-0 pointer-events-none dot-sparks-overlay" style={{ zIndex: 0 }}>
        {sparks.map((s) => (
          <span
            key={s.id}
            className="dot-spark"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.size * 3}px`,
              height: `${s.size * 3}px`,
              opacity: s.opacity,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>
    );
  }, []);

  const handleCtaClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    let hasNavigated = false;
    const goToTaller = () => {
      if (hasNavigated) return;
      hasNavigated = true;
      navigate('/taller-dolor-parto');
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'ads_conversion_Inicio_de_la_tramitaci__1', {
        event_callback: goToTaller,
        event_timeout: 2000,
      });

      window.setTimeout(goToTaller, 2100);
      return;
    }

    goToTaller();
  };

  if (!isVisible) return null;

  return (
    <div
      className="sticky z-40 bg-gradient-to-r from-contessa-600 via-contessa-500 to-contessa-600 text-white overflow-hidden shadow-lg transition-[top] duration-500"
      style={{ top: 'var(--header-visible-height, 0px)' }}
    >
      <button
        type="button"
        aria-label="Cerrar banner"
        title="Cerrar"
        onClick={() => setIsVisible(false)}
        className="absolute right-3 top-3 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:hidden"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      {/* Pattern de fondo sutil */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Puntitos que parpadean tipo estrellitas */}
      {sparkElements}

      <div className="container relative z-10 mx-auto px-6 pb-4 mt-2 md:pb-8 md:pt-8">
        {/* Fecha tipo calendario arriba en mobile */}
        <div className="flex justify-center mb-3 md:hidden">
          <div className="flex items-center gap-3 bg-white/15 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-white/10 shadow-lg">
            <div className="flex flex-col items-center leading-none">
              <span className="text-2xl font-bold banner-text-3d">7</span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-contessa-100">Agosto</span>
            </div>
            <div className="w-px h-8 bg-white/25" />
            <span className="text-sm font-semibold banner-text-3d">19hs</span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Contenido principal */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold font-serif mb-1 banner-text-3d">
              Taller de Manejo del dolor en el parto
            </h3>
            <p className="text-xs md:text-xs text-contessa-50 italic banner-text-3d">
              Un espacio cuidado para comprender y habitar el dolor, creando herramientas para poder transitarlo.
            </p>
            <Link
              to="/taller-dolor-parto"
              onClick={handleCtaClick}
              className="banner-cta-pulse mt-3 inline-flex items-center gap-2 bg-white text-contessa-700 hover:bg-contessa-50 font-bold text-sm px-5 py-2 rounded-full shadow-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Ver más e inscribirme
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          {/* Fecha tipo calendario a la derecha en desktop */}
          <div className="hidden md:flex items-center gap-4 bg-white/15 px-5 py-3 rounded-xl backdrop-blur-sm border border-white/10 shrink-0 shadow-lg">
            <div className="flex flex-col items-center leading-none">
              <span className="text-3xl font-bold banner-text-3d">7</span>
              <span className="text-xs font-semibold uppercase tracking-widest text-contessa-100">Agosto</span>
            </div>
            <div className="w-px h-10 bg-white/25" />
            <div className="flex flex-col items-center leading-tight">
              <span className="text-lg font-bold banner-text-3d">19hs</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
